import { ref, reactive, computed } from 'vue';
import { useImageTemplateStore } from '@/stores/ImageTemplateStore';
import { FORMATS, getPresetTemplates, findPresetTemplate, readableTextColor } from './presets';

const LAST_KEY = 'i7.imageGenerator.last.';

/* Elementos que podem ser ligados e desligados. */
export const TOGGLES = [
  { role: 'cta', label: 'Chamada' },
];

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function readLast(format) {
  try {
    return JSON.parse(localStorage.getItem(LAST_KEY + format));
  } catch (error) {
    return null;
  }
}

function writeLast(format, source) {
  try {
    localStorage.setItem(LAST_KEY + format, JSON.stringify(source));
  } catch (error) {
    return;
  }
}

/*
 * Estado do gerador. O modelo de cada formato fica separado, então alternar
 * entre Story e Publicação não perde o que foi ajustado.
 */
export function useImageEditor(article) {
  const store = useImageTemplateStore();

  const format = ref('story');
  const states = reactive({ story: null, post: null });

  const data = computed(() => ({
    titulo: article.title || '',
    photo: article.cover_image,
  }));

  const formatInfo = computed(() => FORMATS[format.value]);
  const current = computed(() => states[format.value]);
  const config = computed(() => (current.value ? current.value.config : null));
  const elements = computed(() => (config.value ? config.value.elements : []));

  const presets = computed(() => getPresetTemplates(format.value));
  const savedTemplates = computed(() => store.templates.filter(template => template.format === format.value));

  function sameSource(a, b) {
    if (!a || !b || a.type !== b.type) {
      return false;
    }

    if (a.type === 'preset') {
      return a.key === b.key;
    }

    return a.id === b.id;
  }

  function isDefault(source) {
    return sameSource(store.defaults[format.value], source);
  }

  /* Modelos prontos e salvos numa lista só, com o padrão em primeiro. */
  const templateOptions = computed(() => {
    const list = [];

    for (const preset of presets.value) {
      list.push({ source: { type: 'preset', key: preset.key }, name: preset.name, format: preset.format, config: preset.config, preset });
    }

    for (const template of savedTemplates.value) {
      list.push({ source: { type: 'saved', id: template.id }, name: template.name, format: template.format, config: template.config, saved: template });
    }

    const index = list.findIndex(item => isDefault(item.source));

    if (index > 0) {
      list.unshift(list.splice(index, 1)[0]);
    }

    return list;
  });

  const isCurrentDefault = computed(() => !!current.value && isDefault(current.value.source));

  const savedTemplate = computed(() => {
    if (!current.value || current.value.source.type !== 'saved') {
      return null;
    }

    return store.templates.find(template => template.id === current.value.source.id) || null;
  });

  const dirty = computed(() => {
    if (!current.value) {
      return false;
    }

    return JSON.stringify(current.value.config) !== current.value.savedJson;
  });

  /* Só aparecem as opções que o modelo atual tem. */
  const toggles = computed(() => {
    const list = [];

    for (const toggle of TOGGLES) {
      const element = elements.value.find(item => item.role === toggle.role);

      if (!element) {
        continue;
      }

      list.push({ ...toggle, element });
    }

    return list;
  });

  /* Liga ou desliga o elemento e os que dependem dele (a seta da chamada, por exemplo). */
  function setToggle(role, visible) {
    for (const element of elements.value) {
      if (element.role === role || element.linkedTo === role) {
        element.hidden = !visible;
      }
    }
  }

  /* ---------- carregar modelos ---------- */

  function load(formatKey, source, name, templateConfig) {
    states[formatKey] = {
      source,
      name,
      config: clone(templateConfig),
      savedJson: JSON.stringify(templateConfig),
    };

    writeLast(formatKey, source);
  }

  function applyPreset(preset) {
    load(preset.format, { type: 'preset', key: preset.key }, preset.name, preset.config);
  }

  function applySaved(template) {
    load(template.format, { type: 'saved', id: template.id }, template.name, template.config);
  }

  function isActive(source) {
    return !!current.value && sameSource(current.value.source, source);
  }

  function applyOption(option) {
    if (option.saved) {
      applySaved(option.saved);
    } else {
      applyPreset(option.preset);
    }
  }

  function applySource(formatKey, source) {
    if (!source) {
      return false;
    }

    if (source.type === 'saved') {
      const template = store.templates.find(item => item.id === source.id);

      if (template) {
        applySaved(template);
        return true;
      }
    }

    if (source.type === 'preset') {
      const preset = findPresetTemplate(formatKey, source.key);

      if (preset) {
        applyPreset(preset);
        return true;
      }
    }

    return false;
  }

  /* Ordem: modelo padrão, último usado, primeiro modelo pronto. */
  function initialTemplate(formatKey) {
    if (applySource(formatKey, store.defaults[formatKey])) {
      return;
    }

    if (applySource(formatKey, readLast(formatKey))) {
      return;
    }

    applyPreset(getPresetTemplates(formatKey)[0]);
  }

  function isLoaded() {
    return store.loaded;
  }

  /* Com o store já carregado (pré-carga da página) aplica na hora, sem esperar a API. */
  async function init() {
    if (!store.loaded) {
      await store.getTemplates();
    }

    applyInitialTemplates();
  }

  function applyInitialTemplates() {
    for (const formatKey of Object.keys(FORMATS)) {
      initialTemplate(formatKey);
    }
  }

  /* ---------- cores e fontes ---------- */

  function hasFullPhoto() {
    return elements.value.some(element => element.type === 'photo' && !element.hidden && element.w >= 0.99 && element.h >= 0.99);
  }

  function applyPalette(palette) {
    const fullPhoto = hasFullPhoto();

    config.value.background = clone(palette.background);
    config.value.palette = palette.key;

    for (const element of elements.value) {
      if (element.type === 'text') {
        if (element.box && element.box.enabled) {
          element.box.color = palette.accent;
          element.color = palette.accentText;
        } else if (element.onAccent) {
          element.color = palette.accentText;
        } else if (element.onCard) {
          element.color = palette.cardText;
        } else if (fullPhoto) {
          element.color = '#ffffff';
        } else {
          element.color = palette.text;
        }
      }

      if (element.type === 'icon') {
        element.color = fullPhoto ? '#ffffff' : palette.text;
      }

      if (element.type === 'shape') {
        element.color = element.role === 'accent' ? palette.accent : palette.card;
      }

      if (element.type === 'photo' && element.overlay.type !== 'none') {
        element.overlay.color = palette.overlay;
      }
    }
  }

  /* Cor livre: troca só o fundo e ajusta o texto que fica direto sobre ele. */
  function applyCustomColor(color) {
    config.value.background = { type: 'color', color };
    config.value.palette = 'custom';

    if (hasFullPhoto()) {
      return;
    }

    const textColor = readableTextColor(color);

    for (const element of elements.value) {
      const onBackground = element.type === 'icon' || (element.type === 'text' && !element.onCard && !element.onAccent && !(element.box && element.box.enabled));

      if (onBackground) {
        element.color = textColor;
      }
    }
  }

  function applyFontPair(pair) {
    config.value.fontPair = pair.key;

    for (const element of elements.value) {
      /* A chamada mantém a mesma fonte em todos os modelos. */
      if (element.type !== 'text' || element.role === 'cta') {
        continue;
      }

      const font = element.role === 'title' ? pair.title : pair.support;
      element.font = font.font;
      element.weight = font.weight;
      element.italic = !!font.italic;
    }
  }

  /* ---------- salvar ---------- */

  /* Mesmo nome do modelo salvo aberto atualiza; nome novo cria outro modelo. */
  async function save(name) {
    const template = savedTemplate.value;
    let id = null;

    if (template && template.name === name) {
      id = template.id;
    }

    const result = await store.saveTemplate(id, {
      name,
      format: format.value,
      config: current.value.config,
    });

    if (result.success) {
      states[format.value] = {
        source: { type: 'saved', id: result.template.id },
        name: result.template.name,
        config: current.value.config,
        savedJson: JSON.stringify(current.value.config),
      };

      writeLast(format.value, { type: 'saved', id: result.template.id });
    }

    return result;
  }

  async function removeSaved(template) {
    const result = await store.deleteTemplate(template.id);

    if (result.success && current.value && current.value.source.id === template.id) {
      applyPreset(getPresetTemplates(format.value)[0]);
    }

    return result;
  }

  /* Liga ou desliga o modelo aberto como padrão do formato. */
  async function setCurrentAsDefault(enabled) {
    if (!current.value) {
      return { success: false };
    }

    const source = enabled ? current.value.source : null;

    return store.setDefault(format.value, source);
  }

  return {
    format, formatInfo, current, config, data, dirty, toggles,
    templateOptions, savedTemplate, isDefault, isCurrentDefault, isActive, applyOption,
    setToggle, init, isLoaded, applyInitialTemplates, applyPreset, applySaved, applyPalette, applyCustomColor, applyFontPair,
    save, removeSaved, setCurrentAsDefault,
  };
}
